// talkingAvatarSource — turns AvatarNarrationTab's step-1 state into the
// `avatar` field of POST /api/talking-avatar, which takes EXACTLY ONE of
// { avatarId } | { imageUrl } | { videoUrl }.
//
//   library avatar (incl. a saved custom one)  → { avatarId }
//   AI-created this session                    → { avatarId: aiResult.lipsyncAvatarId }
//                                                 else { videoUrl: aiResult.videoUrl }
//   own photo                                  → { imageUrl: uploaded file URL }
//   own video                                  → { videoUrl: uploaded file URL }
//
// Two things must never reach the server: the synthetic `ai-<timestamp>` id the
// tab invents for an AI avatar that has no library row, and a `blob:` URL (the
// tab revokes those, and the server could not read them anyway). An own photo
// or video is `pending` until its upload has produced a server URL.
//
// Result: { ready, pending, kind, payload, thumbUrl, name, aiJobId, reason }

const SYNTHETIC_AI_ID = /^ai-\d+$/;

// What the API accepts: http(s), or our relative asset routes
// (/api/f/…, /api/studio-files/blob/…). Never blob: or data:.
const isServerUrl = (u) =>
  typeof u === "string" &&
  (/^https?:\/\//i.test(u.trim()) || /^\/api\/(f|studio-files\/blob)\//i.test(u.trim()));

function result(fields) {
  return {
    ready: false,
    pending: false,
    kind: null,
    payload: null,
    thumbUrl: null,
    name: null,
    aiJobId: null,
    reason: null,
    ...fields,
  };
}

function fromAiAvatar(avatar, aiResult) {
  const aiJobId = (aiResult && aiResult.jobId) || null;
  const thumbUrl = (avatar && avatar.thumbnailUrl) || null;
  const name = (avatar && avatar.name) || null;
  const lipsyncId = aiResult && aiResult.lipsyncAvatarId;
  if (lipsyncId != null && lipsyncId !== "") {
    return result({
      ready: true,
      kind: "ai",
      payload: { avatarId: lipsyncId },
      thumbUrl,
      name,
      aiJobId,
    });
  }
  const videoUrl = (aiResult && aiResult.videoUrl) || (avatar && avatar.videoUrl);
  if (isServerUrl(videoUrl)) {
    return result({ ready: true, kind: "ai", payload: { videoUrl }, thumbUrl, name, aiJobId });
  }
  // Only a library row id is acceptable here — never the synthetic ai-<ts> one.
  if (avatar && avatar.id != null && !SYNTHETIC_AI_ID.test(String(avatar.id))) {
    return result({
      ready: true,
      kind: "ai",
      payload: { avatarId: avatar.id },
      thumbUrl,
      name,
      aiJobId,
    });
  }
  return result({ kind: "ai", reason: "noAvatar" });
}

function fromUpload(kind, { file, fileUrl, uploading }) {
  if (!file) return result({ kind, reason: "noAvatar" });
  if (uploading) return result({ kind, pending: true, reason: "uploading" });
  if (!isServerUrl(fileUrl)) return result({ kind, reason: "uploadFailed" });
  const payload = kind === "photo" ? { imageUrl: fileUrl } : { videoUrl: fileUrl };
  return result({ ready: true, kind, payload, thumbUrl: kind === "photo" ? fileUrl : null });
}

export function resolveTalkingAvatarSource(state) {
  const {
    selectedAvatarType,
    selectedStockAvatar,
    aiResult,
    sourceType,
    uploadedVideo,
    uploadedVideoFileUrl,
    uploadingVideo,
    uploadedPhoto,
    uploadedPhotoFileUrl,
    uploadingPhoto,
  } = state || {};
  const avatar = selectedStockAvatar || null;

  if (avatar && avatar.isAiGenerated) return fromAiAvatar(avatar, aiResult);

  if (avatar && avatar.id != null) {
    return result({
      ready: true,
      kind: "library",
      payload: { avatarId: avatar.id },
      thumbUrl: avatar.thumbnailUrl || null,
      name: avatar.name || null,
    });
  }

  if (selectedAvatarType !== "custom") return result({ reason: "noAvatar" });

  if (sourceType === "ai") {
    return aiResult && aiResult.videoUrl
      ? fromAiAvatar(null, aiResult)
      : result({ kind: "ai", reason: "noAvatar" });
  }
  if (sourceType === "photo") {
    return fromUpload("photo", {
      file: uploadedPhoto,
      fileUrl: uploadedPhotoFileUrl,
      uploading: uploadingPhoto,
    });
  }
  return fromUpload("video", {
    file: uploadedVideo,
    fileUrl: uploadedVideoFileUrl,
    uploading: uploadingVideo,
  });
}
